export type ScoreBreakdownCollection = "components" | "items";

export type ScoreBreakdownSource =
  | "direct"
  | "derived"
  | "fallback"
  | "default"
  | "missing"
  | "unknown";

export interface ScoreBreakdownCapReason {
  cap: number;
  reason: string;
}

export interface ScoreBreakdownEntry {
  key: string;
  label: string;
  operandId: string | null;
  value: string | number | boolean | null;
  score: number | null;
  weight: number;
  weightPercent: number;
  contribution: number | null;
  source: ScoreBreakdownSource;
  sourceLabel: string;
  measuredAt: string | null;
  fallbackFrom: string | null;
  measurementValueId: string | null;
  warnings: string[];
}

export interface ScoreBreakdown {
  formulaType: string | null;
  collection: ScoreBreakdownCollection;
  rawScore: number | null;
  cappedScore: number | null;
  cap: number | null;
  capReasons: ScoreBreakdownCapReason[];
  warnings: string[];
  entries: ScoreBreakdownEntry[];
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function asString(value: unknown): string | null {
  return typeof value === "string" && value.trim() ? value.trim() : null;
}

function asNumber(value: unknown): number | null {
  return typeof value === "number" && Number.isFinite(value) ? value : null;
}

function asDisplayValue(value: unknown): ScoreBreakdownEntry["value"] {
  if (
    typeof value === "string" ||
    typeof value === "number" ||
    typeof value === "boolean"
  ) {
    return value;
  }
  return null;
}

function appendMessage(messages: Set<string>, value: unknown) {
  if (typeof value === "string" && value.trim()) {
    messages.add(value.trim());
  }
}

function collectMessages(...values: unknown[]): string[] {
  const messages = new Set<string>();
  for (const value of values) {
    if (Array.isArray(value)) {
      value.forEach((item) => appendMessage(messages, item));
    } else {
      appendMessage(messages, value);
    }
  }
  return Array.from(messages);
}

function normalizeSource(value: unknown): ScoreBreakdownSource {
  switch (value) {
    case "direct":
    case "derived":
    case "fallback":
    case "default":
    case "missing":
      return value;
    default:
      return "unknown";
  }
}

export function sourceLabel(source: ScoreBreakdownSource): string {
  switch (source) {
    case "direct":
      return "Direct";
    case "derived":
      return "Computed";
    case "fallback":
      return "Carried forward";
    case "default":
      return "Default";
    case "missing":
      return "Missing";
    case "unknown":
      return "Unknown";
  }
}

function collectCapReasons(value: unknown): ScoreBreakdownCapReason[] {
  if (!Array.isArray(value)) return [];

  return value.flatMap((entry): ScoreBreakdownCapReason[] => {
    if (!isRecord(entry)) return [];
    const cap = asNumber(entry.cap);
    const reason = asString(entry.reason);
    if (cap == null || reason == null) return [];
    return [{ cap, reason }];
  });
}

function chooseCollection(
  detail: Record<string, unknown>,
): [ScoreBreakdownCollection, Record<string, unknown>] | null {
  if (isRecord(detail.components)) return ["components", detail.components];
  if (isRecord(detail.items)) return ["items", detail.items];
  return null;
}

function entryScore(rawEntry: Record<string, unknown>): number | null {
  const hasScore = Object.prototype.hasOwnProperty.call(rawEntry, "score");
  return hasScore ? asNumber(rawEntry.score) : asNumber(rawEntry.value);
}

function buildEntry(
  key: string,
  rawEntry: unknown,
  totalWeight: number,
): ScoreBreakdownEntry | null {
  if (!isRecord(rawEntry)) return null;

  const weight = asNumber(rawEntry.weight);
  if (weight == null || weight <= 0) return null;

  const score = entryScore(rawEntry);
  const source = normalizeSource(rawEntry.source);
  const warnings = collectMessages(rawEntry.warning, rawEntry.warnings);

  return {
    key,
    label: asString(rawEntry.label) ?? key,
    operandId: asString(rawEntry.operand) ?? asString(rawEntry.fieldId),
    value: asDisplayValue(rawEntry.value),
    score,
    weight,
    weightPercent:
      score != null && totalWeight > 0 ? (weight / totalWeight) * 100 : 0,
    contribution:
      score == null || totalWeight <= 0 ? null : (score * weight) / totalWeight,
    source,
    sourceLabel: sourceLabel(source),
    measuredAt: asString(rawEntry.measuredAt),
    fallbackFrom: asString(rawEntry.fallbackFrom),
    measurementValueId: asString(rawEntry.measurementValueId),
    warnings,
  };
}

export function buildScoreBreakdown(detail: unknown): ScoreBreakdown | null {
  if (!isRecord(detail)) return null;

  const collection = chooseCollection(detail);
  if (!collection) return null;

  const [collectionName, rawEntries] = collection;
  const rawEntryPairs = Object.entries(rawEntries).filter(([, value]) =>
    isRecord(value),
  );
  const totalWeight = rawEntryPairs.reduce((sum, [, value]) => {
    if (!isRecord(value) || entryScore(value) == null) return sum;
    const weight = asNumber(value.weight);
    return weight != null && weight > 0 ? sum + weight : sum;
  }, 0);

  const entries = rawEntryPairs
    .map(([key, value]) => buildEntry(key, value, totalWeight))
    .filter((entry): entry is ScoreBreakdownEntry => entry != null);

  if (entries.length === 0) return null;

  return {
    formulaType: asString(detail.formulaType),
    collection: collectionName,
    rawScore: asNumber(detail.rawScore),
    cappedScore: asNumber(detail.cappedScore),
    cap: asNumber(detail.cap),
    capReasons: collectCapReasons(detail.capReasons),
    warnings: collectMessages(detail.warnings),
    entries,
  };
}
